import type { NavStuck, RobotStatus } from '../api/types';
import { toast } from '../components/toast';
import { t } from '../i18n';
import { useLive } from '../live/store';
import { navigateTo } from './nav';

export type StuckView =
  | { kind: 'active'; phase: string; attempt: number; max: number; reason?: string; side?: string; waypoint?: { x: number; y: number } | null }
  | { kind: 'failed'; attempt: number; max: number; reason?: string; location?: NavStuck['location']; goal?: NavStuck['goal']; pointId?: string | null };

const ACTIVE = ['detected', 'announce', 'lookaround', 'replan', 'detour'];

/**
 * Current stuck state from status.navigation.stuck (server truth while recovering) and the latest navStuck event
 * (phase detail; `failed` stays until dismissed or a new episode starts). Older backends without `status.navigation`
 * are driven by events alone.
 */
export function stuckOf(status: RobotStatus | null | undefined, ev: (NavStuck & { t: number }) | undefined): StuckView | null {
  const st = status?.navigation?.stuck ?? null;
  const knowsStatus = Boolean(status && 'navigation' in status);
  const max = (ev?.maxAttempts ?? st?.maxAttempts ?? 2) || 2;
  if (st) {
    const useEv = ev && ACTIVE.includes(String(ev.phase));
    const phase = String(useEv ? ev.phase : st.phase);
    return { kind: 'active', phase, attempt: (useEv ? ev.attempt : undefined) ?? st.attempt ?? 1, max, reason: ev?.reason, ...(phase === 'detour' && useEv ? { side: ev.side, waypoint: ev.waypoint } : {}) };
  }
  if (!ev) return null;
  if (ev.phase === 'failed') return { kind: 'failed', attempt: ev.attempt ?? max, max, reason: ev.reason, location: ev.location, goal: ev.goal, pointId: ev.pointId };
  if (!knowsStatus && ACTIVE.includes(String(ev.phase)))
    return { kind: 'active', phase: String(ev.phase), attempt: ev.attempt ?? 1, max, reason: ev.reason, ...(ev.phase === 'detour' ? { side: ev.side, waypoint: ev.waypoint } : {}) };
  return null;
}

export function useStuck(serial: string): StuckView | null {
  const status = useLive((s) => s.robots[serial]?.status);
  const ev = useLive((s) => s.navStuck[serial]);
  return stuckOf(status, ev);
}

export const stuckPhaseLabel = (phase: string, side?: string) =>
  phase === 'detour'
    ? side === 'left' || side === 'right'
      ? t(`stuck.phase.detour.${side}`)
      : t('stuck.phase.detour')
    : ['detected', 'announce', 'lookaround', 'replan', 'recovered', 'failed'].includes(phase)
      ? t(`stuck.phase.${phase}` as 'stuck.phase.detected')
      : phase;

/** "Blocked — looking around (attempt 1/2)" */
export const stuckText = (v: Extract<StuckView, { kind: 'active' }>) => t('stuck.banner', { phase: stuckPhaseLabel(v.phase, v.side), a: v.attempt, m: v.max });

/** /ws/ui navStuck → store, robot log, toasts, autonomy transcript line. */
export function onNavStuck(serial: string, ev: NavStuck) {
  const live = useLive.getState();
  const robot = live.robots[serial];
  const name = robot?.name ?? serial;
  live.setNavStuck(serial, ev);
  live.addLog(serial, 'event', 'navStuck', `${ev.phase}${ev.attempt ? ` ${ev.attempt}` : ''}${ev.reason ? ` · ${ev.reason}` : ''}`);
  if (robot?.status?.autonomy?.active) {
    const { phase, attempt, maxAttempts, reason, side } = ev;
    live.addAutonomy({ type: 'autonomy', serial, kind: 'navStuck', phase, attempt, maxAttempts, reason, side });
  }
  if (ev.phase === 'recovered') toast(t('stuck.recoveredToast', { name }), 'ok');
  if (ev.phase === 'failed')
    toast(t('stuck.failedToast', { name }), 'err', { label: t('stuck.openRobot'), onClick: () => navigateTo(`/robots/${encodeURIComponent(serial)}`) });
}
